<template>
  <lb-drawer
    :value="value"
    heading="View Response"
    @input="$emit('input', $event)"
    data-testid="view-response-drawer"
  >
    <template v-slot:body>
      <v-row>
        <!-- Request Description -->
        <v-col cols="12" class="font-weight-bold font-size-lg" data-testid="view-response__description">
          {{ requestHeader.description }}
        </v-col>

        <!-- Requested From -->
        <v-col cols="3" class="gray--text">Requested From</v-col>
        <v-col cols="9" data-testid="view-response__owner-name">
          {{ requestHeader.info_owner_detail && requestHeader.info_owner_detail.name }}
        </v-col>

        <!-- Email Id -->
        <v-col cols="3" class="gray--text">Email Id</v-col>
        <v-col cols="9" class="blue--text text-decoration-underline" data-testid="view-response__owner-email">
          {{ requestHeader.info_owner_detail && requestHeader.info_owner_detail.email }}
        </v-col>

        <!-- Type -->
        <v-col cols="3" class="gray--text">Type</v-col>
        <v-col cols="9">
          <v-chip label class="lightgray radius-small" data-testid="view-response__type-chip">
            <span>{{ requestHeader.type }}</span>
          </v-chip>
        </v-col>

        <!-- Priority -->
        <v-col cols="3" class="gray--text">Priority</v-col>
        <v-col cols="9">
          <v-chip
            label
            class="lightgray radius-small"
            v-if="requestHeader.priority"
            data-testid="view-response__priority-chip"
          >
            <span>{{ requestHeader.priority }}</span>
          </v-chip>
        </v-col>

        <!-- Status -->
        <v-col cols="3" class="gray--text">Status</v-col>
        <v-col cols="9">
          <v-chip class="lightyellow yellow--text" v-if="requestHeader.stage === 0">
            <v-icon color="yellow">mdi-circle-small</v-icon>
            <span>Pending Submission</span>
          </v-chip>
          <v-chip class="lightblue blue--text" v-if="requestHeader.stage === 1">
            <v-icon color="blue">mdi-circle-small</v-icon>
            <span>Pending Approval</span>
          </v-chip>
          <v-chip class="lightred error--text" v-if="requestHeader.stage === -1">
            <v-icon color="error">mdi-circle-small</v-icon>
            <span>Cancelled</span>
          </v-chip>
          <v-chip class="lightgreen green--text" v-if="requestHeader.stage === 2">
            <v-icon color="green">mdi-circle-small
            </v-icon>
            <span>Closed</span>
          </v-chip>
        </v-col>

        <!-- Tabs Section -->
        <v-col cols="12">
          <v-tabs
            v-model="activeTab"
            bg-color="primary"
            class="border-bottom d-flex align-center justify-center"
            color="caccounting"
            height="34"
          >
            <v-tab :key="0" class="text-transform-none pa-0 px-2 justify-start">
              Recent Activity
              <v-icon class="mr-2">mdi-information-outline</v-icon>
            </v-tab>
            <v-tab
              :key="1"
              class="text-transform-none pa-0 px-2 justify-start"
              @click="$emit('open-comments')"
              data-testid="view-response__comments-tab"
            >
              Comments
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <!-- Recent Activity Tab -->
            <v-tab-item :key="0">
              <div class="d-flex flex-row flex-wrap">
                <!-- File Detail Card -->
                <template v-if="headerAttachments.length > 0">
                  <div
                    v-for="(file, fileIdx) in headerAttachments"
                    :key="resolveAttachmentKey(file, fileIdx)"
                    class="pa-3 mt-2 mr-1 border-on radius-small d-flex align-center gap-2"
                    style="flex: 0 0 32%; max-width: 32%; min-width: 0;"
                    :data-testid="`view-response__attachment-card--${fileIdx}`"
                  >
                    <div class="d-flex flex-column" style="flex: 1; min-width: 0;">
                      <p class="ma-0 black--text font-size-md single-line text-truncate">
                        {{ file.uploadname || file.originalname || file.file_name || `Document ${fileIdx + 1}` }}
                      </p>
                      <p class="font-size-xm ma-0" v-if="file.date">{{ formatDate(file.date) }}</p>
                    </div>
                    <div style="flex-shrink: 0;">
                      <v-btn
                        @click="$emit('download-file', resolveAttachmentId(file))"
                        icon
                        class="cursor-pointer radius-mini border-on"
                        :data-testid="`view-response__download-file-btn--${fileIdx}`"
                      >
                        <v-icon>mdi-download</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>

                <!-- Response Card -->
                <div v-if="response && response.response" class="pa-3 mt-2 mr-1 border-on radius-small d-flex flex-column gap-2" style="flex: 0 0 32%; max-width: 32%;">
                  <p class="ma-0 black--text font-size-md mb-3"><b>Response</b></p>
                  <div class="blue--text mt-1" data-testid="view-response__response-message">
                    {{ response && response.response }}
                  </div>
                </div>

                <!-- Remark Card -->
                <div v-if="response && response.remark" class="pa-3 mt-2 border-on radius-small d-flex flex-column gap-2" style="flex: 0 0 32%; max-width: 32%;">
                  <p class="ma-0 black--text font-size-md mb-3"><b>Remark</b></p>
                  <div class="blue--text mt-1" data-testid="view-response__remark-message">
                    {{ response && response.remark }}
                  </div>
                </div>
              </div>
            </v-tab-item>

            <!-- Comments Tab -->
            <v-tab-item :key="1" class="all-height">
              <v-card-text
                class="border-on d-flex flex-column all-height justify-space-between comments-container overflow-scroll pa-2"
              >
                <div v-if="(requestHeader.comments || []).length === 0" class="title text-center my-5 grey--text">
                  No Comments added yet
                </div>
                <div v-else class="mb-8">
                  <perfect-scrollbar>
                    <div
                      v-for="(comment, idx) in requestHeader.comments"
                      :key="idx"
                      :class="`d-flex all-width ${currentUserId !== comment.user ? '' : 'flex-column justify-end'}`"
                    >
                      <div class="mx-1 caption d-flex align-center justify-end">
                        {{ formatDateTime(comment.created_at) }}
                        <v-tooltip bottom content-class="tooltip-bottom">
                          <template v-slot:activator="{ on, attrs }">
                            <v-avatar
                              size="24"
                              class="lightpurple caccounting--text pa-2 ml-2"
                              v-bind="attrs"
                              v-on="on"
                            >
                              {{ getUserInitials(comment.user) }}
                            </v-avatar>
                          </template>
                          <span>{{ getUserName(comment.user) }}</span>
                        </v-tooltip>
                      </div>
                      <div
                        :class="`lightpurple radius-small px-4 py-2 my-2 ${currentUserId !== comment.user ? 'grey lighten-2' : 'text-left'}`"
                      >
                        {{ comment && comment.comment }}
                      </div>
                    </div>
                  </perfect-scrollbar>
                </div>

                <!-- Comment Input -->
                <div
                  v-if="canAddComments"
                  class="d-flex align-center"
                  style="position: absolute; bottom: 2%; width: 95%; background: white"
                >
                  <lb-string
                    :value="commentText"
                    @input="$emit('update-comment', $event)"
                    @keyup.enter.native="handleCommentKeyPress"
                    label="Write here..."
                    :error="commentError"
                    hidedetails
                    :disabled="isDisabledStage"
                    data-testid="view-response__comment-input"
                  />
                  <v-btn
                    small
                    color="caccounting"
                    class="white--text ml-2"
                    @click="$emit('add-comment')"
                    :loading="commentsLoading"
                    :disabled="isDisabledStage"
                    data-testid="view-response__send-comment-btn"
                  >
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                </div>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-col>
      </v-row>
    </template>

    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn
        class="black white--text"
        @click="$emit('input', false)"
        data-testid="view-response__close-btn"
      >
        <span>Close</span>
      </v-btn>
    </template>
  </lb-drawer>
</template>

<script>
import {
  collectAttachmentEntries,
  resolveAttachmentIds,
  resolveAttachmentKey,
} from '@/modules/financialclose/utils/informationManagerComponentUtils';

const resolveAttachmentKeyUtil = resolveAttachmentKey;

const VIEW_RESPONSE_ATTACHMENT_FIELD_ORDER = ['_id', 'uploadid', 'fileid', 'file_id', 'id', 'upload_id'];

export default {
  name: 'ViewResponseDrawer',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    requestHeader: {
      type: Object,
      default: () => ({}),
    },
    response: {
      type: Object,
      default: () => ({ response: '', remark: '' }),
    },
    currentUserId: {
      type: String,
      default: '',
    },
    canAddComments: {
      type: Boolean,
      default: false,
    },
    commentText: {
      type: String,
      default: '',
    },
    commentError: {
      type: String,
      default: '',
    },
    commentsLoading: {
      type: Boolean,
      default: false,
    },
    userList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeTab: 0,
    };
  },
  computed: {
    isDisabledStage() {
      return [-1, 2].indexOf(this.requestHeader.stage) > -1;
    },
    headerAttachments() {
      return collectAttachmentEntries(this.requestHeader || {});
    },
  },
  methods: {
    /**     * Handles Enter key press in comment input
     */
    handleCommentKeyPress(event) {
      if (event && !event.shiftKey && this.commentText && this.commentText.trim()) {
        event.preventDefault();
        this.$emit('add-comment');
      }
    },
    /**     * Formats a date using Nova formatter
     * @param {string|Date} date - The date to format
     * @returns {string} Formatted date string
     */
    formatDate(date) {
      return this.$nova.formatDate(date);
    },

    /**
     * Formats a datetime using Nova formatter
     * @param {string|Date} date - The datetime to format
     * @returns {string} Formatted datetime string
     */
    formatDateTime(date) {
      return this.$nova.formatDateTime(date, 'DD MMM yyyy hh:mm A');
    },

    /**
     * Gets user initials from user ID
     * @param {string} userId - The user ID
     * @returns {string} User initials or 'NA'
     */
    getUserInitials(userId) {
      const user = this.userList.find((u) => u._id === userId);
      if (!user || !user.name) return 'NA';
      return user.name === 'NA' ? 'NA' : user.name.charAt(0).toUpperCase();
    },

    /**
     * Gets user name from user ID
     * @param {string} userId - The user ID
     * @returns {string} User name or 'NA'
     */
    getUserName(userId) {
      const user = this.userList.find((u) => u._id === userId);
      return user?.name || 'NA';
    },
    resolveAttachmentId(file) {
      const ids = resolveAttachmentIds(file, VIEW_RESPONSE_ATTACHMENT_FIELD_ORDER);
      return ids.length > 0 ? ids[0] : null;
    },
    resolveAttachmentKey(file, index) {
      return resolveAttachmentKeyUtil(file, index);
    },
  },
};
</script>

<style lang="scss" scoped>
.comments-container {
  height: 30vh;
}
</style>
